// IMPORTANT: Replace <your-api-id> with your actual API Gateway ID in us-east-2
const API_GATEWAY_URL = "https://057mg3wbn3.execute-api.us-east-2.amazonaws.com/default/GeneratePresignedUrl"; // Must match your deployed API Gateway endpoint and region

export interface UploadProgress {
  loaded: number;
  total: number;
}

export class S3Service {
  /**
   * Generate a presigned URL for direct upload to S3
   */
  static async getPresignedUrl(fileName: string, fileType: string): Promise<{ uploadURL: string, key: string }> {
    const response = await fetch(API_GATEWAY_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fileName, fileType }),
    });

    if (!response.ok) {
      throw new Error("Failed to get presigned URL");
    }

    const data = await response.json();
    return { uploadURL: data.uploadURL, key: data.key };
  }

  /**
   * Upload file directly to S3 using presigned URL
   */
  static async uploadFile(
    file: File,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<{ fileUrl: string, key: string }> {
    const { uploadURL, key } = await this.getPresignedUrl(file.name, file.type);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable && onProgress) {
          onProgress({
            loaded: event.loaded,
            total: event.total,
          });
        }
      });

      xhr.addEventListener("load", () => {
        if (xhr.status === 200) {
          const url = uploadURL.split("?")[0];
          resolve({ fileUrl: url, key });
        } else {
          reject(new Error(`Upload failed with status: ${xhr.status}`));
        }
      });

      xhr.addEventListener("error", () => {
        reject(new Error("Upload failed"));
      });

      xhr.open("PUT", uploadURL);
      xhr.send(file);
    });
  }

  /**
   * Validate file before upload
   */
  static validateFile(file: File): { isValid: boolean; error?: string } {
    const maxSize = 100 * 1024 * 1024; // 100MB
    const allowedTypes = [
      "image/*",
      "application/pdf",
      "text/*",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    if (file.size > maxSize) {
      return { isValid: false, error: "File size must be less than 100MB" };
    }

    const isAllowedType = allowedTypes.some((type) => {
      if (type.endsWith("/*")) {
        return file.type.startsWith(type.slice(0, -1));
      }
      return file.type === type;
    });

    if (!isAllowedType) {
      return { isValid: false, error: "File type not allowed" };
    }

    return { isValid: true };
  }

  /**
   * Get a presigned URL for downloading a file from S3
   * @param key The S3 object key (e.g., 'uploads/filename.txt')
   * @returns The presigned download URL
   */
  static async getPresignedDownloadUrl(key: string): Promise<string> {
    // TODO: Replace with your actual API Gateway endpoint for download URLs
    const API_GATEWAY_DOWNLOAD_URL = "https://your-api-gateway-url/download-url";
    const response = await fetch(API_GATEWAY_DOWNLOAD_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ key }),
    });
    if (!response.ok) throw new Error("Failed to get download URL");
    const data = await response.json();
    return data.url;
  }
} 